
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const PricingPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Pricing Approach
              </h1>
              <p className="text-lg md:text-xl text-white/80">
                Transparent, minute-based pricing with done-for-you setup and ongoing management. No hidden fees, no long-term contracts required.
              </p>
            </div>
          </div>
        </section>

        {/* How Pricing Works */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 mb-12 shadow-lg">
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6">How pricing works</h2>
              <div className="space-y-6 text-gray-600">
                <p>
                  Our pricing has two components: <strong className="text-navy">setup/management</strong> and <strong className="text-navy">usage</strong>.
                </p>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-navy mb-3">Setup & Management</h3>
                  <p className="text-sm">
                    One-time setup fee covers call flow design, integration configuration, script development, testing, and deployment. Monthly management fee covers ongoing optimization, QA reviews, and support. Both scale based on complexity.
                  </p>
                </div>
                <div className="bg-teal/10 rounded-xl p-6">
                  <h3 className="font-semibold text-navy mb-3">Usage (Per-Minute)</h3>
                  <p className="text-sm">
                    You pay for the minutes used. This scales naturally with your call volume - busy season costs more, slow periods cost less. No paying for idle capacity like with staff.
                  </p>
                </div>
                <p>
                  This model means you get the reliability of a fully-managed solution without the fixed headcount costs. Scale up during peak season, scale down when it's quiet.
                </p>
              </div>
            </div>

            {/* What Affects Cost */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-navy mb-8 text-center">What affects your cost</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Call volume</h3>
                      <p className="text-sm text-gray-600">Higher volume means more minutes used. We help you estimate based on your current call data.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-git-branch-line text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Number of call flows</h3>
                      <p className="text-sm text-gray-600">Simple intake vs. complex routing with multiple branches affects setup and management.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-plug-line text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Integrations required</h3>
                      <p className="text-sm text-gray-600">Standard CRM/calendar integrations are included. Custom integrations may require additional setup.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-translate-2 text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Languages</h3>
                      <p className="text-sm text-gray-600">English is standard. Multilingual support (Spanish, etc.) available for additional coverage.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Compliance requirements</h3>
                      <p className="text-sm text-gray-600">Industries with specific compliance needs (legal, healthcare) may require additional configuration.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Coverage hours</h3>
                      <p className="text-sm text-gray-600">After-hours only vs. 24/7 coverage affects usage patterns and pricing.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-navy text-white rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl font-bold mb-6">What's included</h2>
              <p className="text-white/80 mb-8">
                This isn't DIY software. It's a fully-managed service with everything you need to handle calls reliably.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Custom-built call flows for your business</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Full integration with your phone system</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">CRM and calendar connectivity</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Unlimited call recordings and transcripts</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Real-time monitoring and alerting</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Weekly optimization and QA reviews</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Dedicated account support</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-teal text-xl"></i>
                  </div>
                  <span className="text-white/90">Monthly performance reports</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6">Get a custom quote</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Every business is different. Book a call to discuss your specific needs and get pricing tailored to your call volume and requirements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="https://tidycal.com/instavisible/30-minute-meeting" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-10 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                Book a Demo
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="tel:+16787376473" 
                className="px-10 py-4 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200 text-lg whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
